import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useNotifications } from 'hooks/useNotifications';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { ItineraryManagerApiService } from 'services/ItineraryManagerApi';

export const useVouchersV2AccessListQueries = (
  itineraryManagerApi: ItineraryManagerApiService<AxiosWrapper<AxiosInstance>>,
  voucherUuid: string | undefined
) => {
  const notificationOps = useNotifications();
  const queryClient = useQueryClient();

  return {
    addToVoucherV2AccessList: useMutation({
      mutationFn: async ({
        voucherUuid,
        firstName,
        lastName,
        email,
      }: {
        voucherUuid: string;
        firstName: string;
        lastName: string;
        email: string;
      }) => {
        return itineraryManagerApi.addToVoucherV2AccessList(voucherUuid, firstName, lastName, email);
      },
      onSuccess: () => {
        notificationOps.showSuccessNotification('Authorized user successfully.');
        queryClient.resetQueries({ queryKey: ['getVoucherV2Access', voucherUuid] });
        queryClient.refetchQueries({ queryKey: ['getVoucherV2Access', voucherUuid] });
      },
      onError: (error) => {
        // @ts-ignore
        if (error.response?.data.errors[0].message === 'Person already in access list') {
          notificationOps.showErrorNotification('User already in access list');
          return;
        }
        notificationOps.showErrorNotification('Failed to add authorized user');
      },
    }),
    removeFromVoucherV2AccessList: useMutation({
      mutationFn: async (email: string) => {
        if (!voucherUuid) {
          return;
        }
        return itineraryManagerApi.removeFromVoucherV2AccessList(voucherUuid, email);
      },
      onSuccess: () => {
        notificationOps.showSuccessNotification('User removed.');
        queryClient.resetQueries({ queryKey: ['getVoucherV2Access', voucherUuid] });
        queryClient.refetchQueries({ queryKey: ['getVoucherV2Access', voucherUuid] });
      },
      onError: () => {
        notificationOps.showErrorNotification('Failed to remove user from access list');
      },
    }),
    getVoucherV2AccessList: useQuery({
      queryKey: ['getVoucherV2Access', voucherUuid],
      queryFn: async () => {
        if (!voucherUuid) {
          return;
        }
        return itineraryManagerApi.getVoucherV2AccessList(voucherUuid);
      },
    }),
  };
};
